import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { TimelineStageRow } from '@/components/howItWorks/TimelineStageRow';
import { STAGES } from '@/lib/howItWorksContent';
import { HOW_IT_WORKS } from '@/constants/testIds';

// Vertical scroll-triggered dual-track timeline. Tracks which stage is in
// view via IntersectionObserver (paused while the voice guide is actively
// driving `activeStage`, so playback doesn't fight the scroll observer).
export const DualTrackTimeline = forwardRef(function DualTrackTimeline(
  { audience, activeStage, onActiveStageChange, gateAutoActive = false },
  ref
) {
  const rowRefs = useRef({});

  useImperativeHandle(ref, () => ({
    scrollToStage(slug) {
      const el = rowRefs.current[slug];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  }));

  useEffect(() => {
    if (gateAutoActive) return undefined;
    // Scrollspy pattern: a thin horizontal band around vertical center of the
    // viewport acts as the "trigger line". Whichever stage row's box crosses
    // that band is the active one — this holds up regardless of how tall
    // each row is (a flat 0.45 area-threshold breaks down for tall rows,
    // since several can satisfy it at once).
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const slug = entry.target.getAttribute('data-stage-slug');
            if (slug) onActiveStageChange(slug);
          }
        });
      },
      { threshold: 0, rootMargin: '-45% 0px -45% 0px' }
    );
    Object.values(rowRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [gateAutoActive, onActiveStageChange, audience]);

  return (
    <div id="timeline" className="relative" data-testid={HOW_IT_WORKS.timelineSection}>
      <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-[hsl(var(--lxra-border))]" aria-hidden="true" />
      {STAGES.map((stage, i) => (
        <TimelineStageRow
          key={stage.slug}
          stage={stage}
          index={i}
          audience={audience}
          isActive={activeStage === stage.slug}
          registerRef={(el) => {
            rowRefs.current[stage.slug] = el;
          }}
        />
      ))}
    </div>
  );
});
